<template>
    <div>
        <editor-content
            :editor="editor"
            class="
                coaching-instructions
                mt-1
                block
                w-full
                border border-gray-300
                rounded-md
                shadow-sm
                py-2
                px-3
                min-h-[120px]
                text-sm
                focus-within:ring-purple-500 focus-within:border-purple-500
            "
        />
        <div class="flex items-center gap-3 mt-1">
            <p class="text-xs text-gray-400">
                Type <span class="font-mono">/</span> to insert a line style,
                route, formation, player or background.
            </p>
            <label
                class="
                    text-xs text-purple-700
                    hover:underline
                    cursor-pointer
                    whitespace-nowrap
                    ml-auto
                "
            >
                <input
                    type="file"
                    accept=".txt,.md,.markdown,.csv,text/plain"
                    class="hidden"
                    @change="handleFile"
                />
                Upload instructions (.txt / .md)
            </label>
        </div>
        <span v-if="uploadName" class="text-[11px] text-gray-400"
            >Loaded: {{ uploadName }}</span
        >
        <input type="hidden" :name="name" :value="json" />
    </div>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from "vue";
import { useEditor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { Mention } from "@tiptap/extension-mention";
import { coachingSuggestion } from "./forms/tiptap/coachingSuggestion";

const props = defineProps({
    name: { type: String, default: "custom_instructions" },
    // Stored TipTap document JSON (string or object), or plain text fallback.
    value: { type: [String, Object], default: "" },
    placeholder: {
        type: String,
        default:
            'Add coaching context for this team… e.g. "Use /multilineDotted for motion; our base look is /Shotgun vs /3-4".',
    },
});

const json = ref("");
const uploadName = ref("");

// Load a plain-text/markdown call-sheet file into the editor (client-side, no
// upload). Each line becomes a paragraph so the glossary keeps its structure.
function handleFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    uploadName.value = file.name;
    const reader = new FileReader();
    reader.onload = () => {
        const text = String(reader.result || "");
        const esc = (s) =>
            s
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");
        const html = text
            .split(/\r?\n/)
            .map((l) => `<p>${l.trim() ? esc(l) : "<br>"}</p>`)
            .join("");
        if (editor.value) {
            editor.value.commands.setContent(html);
            json.value = JSON.stringify(editor.value.getJSON());
        }
    };
    reader.readAsText(file);
    e.target.value = ""; // allow re-uploading the same file
}

function parseContent(val) {
    if (!val) return "";
    if (typeof val === "object") return val;
    try {
        return JSON.parse(val);
    } catch {
        return val; // legacy/plain text
    }
}

// A mention node stores the whole vocab item under attrs.id; show its token.
const tokenOf = (p) =>
    p.node.attrs.id?.id ?? p.node.attrs.id?.name ?? p.node.attrs.id ?? "";

const editor = useEditor({
    content: parseContent(props.value),
    extensions: [
        StarterKit,
        Placeholder.configure({ placeholder: props.placeholder }),
        Mention.configure({
            suggestion: coachingSuggestion,
            HTMLAttributes: {
                class: "font-semibold mx-1 text-purple-800 bg-purple-100 rounded px-1.5 py-0.5",
            },
            renderLabel: (p) => `/${tokenOf(p)}`,
            renderText: (p) => tokenOf(p),
        }),
    ],
    editorProps: { attributes: { class: "outline-none min-h-[100px]" } },
    onUpdate: ({ editor }) => {
        json.value = JSON.stringify(editor.getJSON());
    },
});

// Serialize once the editor is ready, and re-hydrate if the parent swaps in a
// stored value (e.g. opening the edit modal) without clobbering active typing.
watch(editor, (e) => {
    if (e) json.value = JSON.stringify(e.getJSON());
});
watch(
    () => props.value,
    (val) => {
        if (!editor.value) return;
        const next = parseContent(val);
        const nextStr = typeof next === "string" ? next : JSON.stringify(next);
        const curStr = JSON.stringify(editor.value.getJSON());
        if (nextStr && nextStr !== curStr) {
            editor.value.commands.setContent(next);
            json.value = JSON.stringify(editor.value.getJSON());
        }
    }
);

onBeforeUnmount(() => editor.value?.destroy());
</script>
